import { Link, useNavigate, useParams } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { formatDate, getMovieById, getRelatedMovies, getRelatedNews } from '@/utils/content';

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const movie = id ? getMovieById(id) : undefined;

  if (!movie) {
    return (
      <PageLayout>
        <div className="mx-auto flex w-full max-w-3xl flex-col items-center justify-center px-4 py-28 text-center">
          <span className="flex h-20 w-20 items-center justify-center rounded-full bg-background-100 text-foreground-400">
            <i className="ri-file-unknow-line text-4xl" />
          </span>
          <h1 className="mt-6 font-heading text-2xl font-bold text-foreground-950">该影视作品不存在</h1>
          <p className="mt-3 text-sm text-foreground-500">
            你访问的作品可能已下架或链接有误，不妨回到分类页看看其他精彩内容。
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/category"
              className="cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              返回影视分类
            </Link>
            <Link
              to="/"
              className="cursor-pointer whitespace-nowrap rounded-md border border-background-200 px-6 py-2.5 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              回到首页
            </Link>
          </div>
        </div>
      </PageLayout>
    );
  }

  const relatedMovies = getRelatedMovies(movie, 6);
  const relatedNews = getRelatedNews(movie, 4);

  const infoRows = [
    { label: '类型', value: `${movie.type} · ${movie.genres.join(' / ')}` },
    { label: '导演', value: movie.director },
    { label: '主演', value: movie.actors.join(' / ') },
    { label: '上映时间', value: formatDate(movie.releaseDate) },
    { label: '片长', value: movie.duration },
    { label: '地区', value: movie.region },
  ];

  return (
    <PageLayout>
      <div className="relative w-full">
        <div className="absolute inset-0 h-72 overflow-hidden md:h-96">
          <img src={movie.backdrop} alt={`${movie.title}剧照`} className="h-full w-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/80 to-foreground-950/60" />
        </div>

        <div className="relative mx-auto w-full max-w-6xl px-4 pt-6 md:px-8">
          <Breadcrumb
            items={[{ label: '首页', to: '/' }, { label: '影视分类', to: '/category' }, { label: movie.title }]}
          />

          <div className="mt-10 grid grid-cols-1 gap-8 pb-10 md:grid-cols-[260px_1fr] md:pt-16">
            <div className="mx-auto w-44 md:mx-0 md:w-full">
              <div className="relative aspect-[2/3] w-full overflow-hidden rounded-lg border border-background-200 bg-background-200 shadow-lg">
                <img
                  src={movie.poster}
                  alt={`${movie.title}封面`}
                  title={`${movie.title} - 红杏影视`}
                  className="h-full w-full object-cover object-top"
                />
              </div>
            </div>

            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
                  {movie.type}
                </span>
                <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs font-medium text-secondary-900">
                  {movie.region}
                </span>
                <span className="rounded-md bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-800">
                  {movie.year}
                </span>
              </div>

              <h1 className="mt-3 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">
                {movie.title}
              </h1>
              <p className="mt-1 text-sm text-foreground-500">{movie.originalTitle}</p>

              <div className="mt-4 flex flex-wrap items-center gap-5">
                <span className="flex items-baseline gap-1 text-accent-600">
                  <span className="font-heading text-2xl font-bold">{movie.rating.toFixed(1)}</span>
                  <span className="text-xs text-foreground-500">/ 10 观众评分</span>
                </span>
                <span className="flex items-center gap-1 text-sm text-foreground-600">
                  <i className="ri-fire-line text-primary-500" />
                  热度 {movie.heat.toLocaleString()}
                </span>
              </div>

              <p className="mt-5 text-sm leading-relaxed text-foreground-700 md:text-base">{movie.summary}</p>

              <div className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 rounded-lg border border-background-200 bg-background-50 p-5 sm:grid-cols-2">
                {infoRows.map((row) => (
                  <div key={row.label} className="flex gap-3 text-sm">
                    <span className="shrink-0 text-foreground-500">{row.label}</span>
                    <span className="font-medium text-foreground-900">{row.value}</span>
                  </div>
                ))}
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
                >
                  <i className="ri-play-circle-line text-lg" />
                  在线观看（演示）
                </button>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-200 px-6 py-3 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
                >
                  <i className="ri-arrow-left-line" />
                  返回上一页
                </button>
              </div>
              <p className="mt-3 flex items-center gap-1.5 text-xs text-foreground-400">
                <i className="ri-information-line" />
                本站为影视资讯展示平台，播放入口仅为演示效果，不提供实际播放服务。
              </p>

              <div className="mt-5 flex flex-wrap gap-2">
                {movie.tags.map((tag) => (
                  <span key={tag} className="rounded-full bg-background-100 px-3 py-1 text-xs text-foreground-600">
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-6xl px-4 pb-4 md:px-8">
        <section className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
          <h2 className="font-heading text-xl font-bold text-foreground-950">剧情简介</h2>
          <div className="mt-4 space-y-4 text-sm leading-loose text-foreground-700 md:text-base">
            <p>{movie.synopsis}</p>
          </div>
        </section>

        <section className="mt-8">
          <h2 className="mb-4 font-heading text-xl font-bold text-foreground-950">最新相关资讯</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {relatedNews.map((article) => (
              <Link
                key={article.id}
                to={`/news/${article.id}`}
                className="group flex cursor-pointer items-center gap-4 rounded-lg border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-200"
              >
                <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md bg-background-200">
                  <img
                    src={article.cover}
                    alt={article.title}
                    className="h-full w-full object-cover object-top"
                  />
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="line-clamp-2 text-sm font-medium leading-snug text-foreground-900 group-hover:text-primary-600">
                    {article.title}
                  </h3>
                  <p className="mt-1.5 text-xs text-foreground-500">
                    {article.category} · {formatDate(article.date)}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="mt-10">
          <div className="mb-4 flex items-end justify-between">
            <h2 className="font-heading text-xl font-bold text-foreground-950">相关推荐</h2>
            <Link
              to="/category"
              className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
            >
              更多同类作品
              <i className="ri-arrow-right-s-line" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {relatedMovies.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </section>
      </div>
    </PageLayout>
  );
}